import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import VenueCard from '../components/VenueCard';

import '../styles/Preferences.css';

const Preferences = () => {
  const { user_id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const forecastRef = useRef(null);

  // Retrieve the venues selected on the Distance page
  const selectedVenues = location.state?.selectedVenues || [];

  // Today's date in YYYY-MM-DD
  const today = new Date().toISOString().split('T')[0];
  // Default end date: one week from today
  const defaultEndDate = new Date();
  defaultEndDate.setDate(defaultEndDate.getDate() + 7);
  const defaultEndDateStr = defaultEndDate.toISOString().split('T')[0];

  // Weather preferences
  const [preferences, setPreferences] = useState({
    temperature: '',
    humidity: '',
    sunClouds: '',
    sunset: '',
    wind: '',
    precipitation: '',
    startDate: today,
    endDate: defaultEndDateStr,
  });

  // Loading & results states
  const [isLoading, setIsLoading] = useState(false);
  const [forecastResults, setForecastResults] = useState(null);
  const [apiError, setApiError] = useState(null);

  // Track the user’s selected date for each venue
  const [selectedMatches, setSelectedMatches] = useState({});

  // Control which “view” is showing
  const [showForm, setShowForm] = useState(true);   // Initially show form
  const [showResults, setShowResults] = useState(false);

  const handlePreferenceChange = (key, value) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
  };

  const handleSeeForecast = async () => {
    setIsLoading(true);
    setApiError(null);

    // Hide the form and results (if any), so we can display the spinner & message
    setShowForm(false);
    setShowResults(false);

    // Dummy values for demonstration
    const user_id = "test_user";
    const start_time = preferences.startDate
      ? `${preferences.startDate} 00:00:00`
      : "2025-03-01 00:00:00";
    const end_time = preferences.endDate
      ? `${preferences.endDate} 23:59:59`
      : "2025-03-07 23:59:59";
    const session_cookie = "test";

    const baseUrl = "https://api.bestday2marry.com/get_multiple_forecast_matches/";
    const params = new URLSearchParams();
    params.append("user_id", user_id);
    params.append("start_time", start_time);
    params.append("end_time", end_time);
    params.append("temperature", preferences.temperature);
    params.append("humidity", preferences.humidity);
    params.append("sunClouds", preferences.sunClouds);
    params.append("wind", preferences.wind);
    params.append("precipitation", preferences.precipitation);
    params.append("sunset_param", preferences.sunset);
    params.append("session_cookie", session_cookie);
    selectedVenues.forEach(venue => params.append("venues", venue));
    params.append("scale", "daily");

    try {
      const response = await fetch(`${baseUrl}?${params.toString()}`);
      const data = await response.json();

      if (data.status === 'success') {
        setForecastResults(data.results);
        setShowResults(true);  // Show results once we have them
      } else {
        setApiError(data.message || "Error fetching forecast");
      }
    } catch (error) {
      console.error("Error fetching forecast:", error);
      setApiError("Error fetching forecast. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Scroll to top of results once we load them
  useEffect(() => {
    if (forecastResults && forecastRef.current) {
      setTimeout(() => {
        forecastRef.current.scrollIntoView({ behavior: 'smooth' });
      }, 0);
    }
  }, [forecastResults]);

  const handleNext = async () => {
    // Build an array of selections from selectedMatches
    const selections = Object.entries(selectedMatches).map(([venueName, match]) => ({
      name: venueName,
      // Convert forecast_date (timestamp string) to YYYY-MM-DD
      date: new Date(Number(match.forecast_date)).toISOString().split('T')[0],
    }));

    try {
      // 1) Store user’s venue/date selections
      const storeResponse = await fetch('https://api.bestday2marry.com/store_user_preferences/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id, selected_venues: selections }),
      });

      const storeJson = await storeResponse.json();
      if (storeJson.status === 'success') {
        // 2) Check account status
        const statusResponse = await fetch(`https://api.bestday2marry.com/account_status/?user_id=${user_id}`, {
          method: 'GET',
          credentials: 'include'
        });
        const statusData = await statusResponse.json();

        if (statusData.status === 'success') {
          const subscriptionPlan = statusData.account_status.subscription?.plan;
          if (subscriptionPlan === 'essentials') {
            // 3) Attempt to use a credit
            const useCreditResponse = await fetch('https://api.bestday2marry.com/use_credit/', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ user_id })
            });
            const creditData = await useCreditResponse.json();

            if (creditData.status !== 'success') {
              console.error("Failed to use credit:", creditData.message);
            } else {
              console.log("Credit used successfully. Credits remaining:", creditData.credits_remaining);
            }
          }
        } else {
          console.error("Error fetching account status:", statusData.message);
        }

        // 4) Finally, navigate to the dashboard
        navigate(`/dashboard/${user_id}`, {
          state: { selectedMatches, forecastResults, preferences },
        });
      } else {
        console.error('Error saving selections:', storeJson.message);
      }
    } catch (error) {
      console.error('Error saving selections:', error);
    }
  };

  // If user wants to go back and adjust preferences
  const handleReturnToForm = () => {
    setForecastResults(null);
    setApiError(null);
    setShowForm(true);
    setShowResults(false);
  };

  return (
    <div className="preferences-page-main-container">
      <header className="logo">
        <a href="/">BestDay2Marry</a>
      </header>

      {/* 1) Show the form if showForm is true and we are not fetching */}
      {showForm && !isLoading && !showResults && (
        <div className="preferences-page-container">
          <h2 className="page-title">What is Your Perfect Weather?</h2>
          <p className="page-description">
            Welcome! This form is designed to help you find the best wedding dates
            based on your chosen venue(s) and your ideal weather conditions...
          </p>

          <form className="preferences-form">
            {/* Temperature Preference */}
            <fieldset className="preference-group">
              <legend>Temperature</legend>
              <label>
                <input
                  type="radio"
                  name="temperature"
                  value="cool"
                  checked={preferences.temperature === 'cool'}
                  onChange={(e) => handlePreferenceChange('temperature', e.target.value)}
                />
                Cool (65-69°F)
              </label>
              <label>
                <input
                  type="radio"
                  name="temperature"
                  value="warm"
                  checked={preferences.temperature === 'warm'}
                  onChange={(e) => handlePreferenceChange('temperature', e.target.value)}
                />
                Warm (70-75°F)
              </label>
              <label>
                <input
                  type="radio"
                  name="temperature"
                  value="hot"
                  checked={preferences.temperature === 'hot'}
                  onChange={(e) => handlePreferenceChange('temperature', e.target.value)}
                />
                Hot (76-85°F)
              </label>
            </fieldset>

            {/* Humidity Preference */}
            <fieldset className="preference-group">
              <legend>Humidity</legend>
              <label>
                <input
                  type="radio"
                  name="humidity"
                  value="humid"
                  checked={preferences.humidity === 'humid'}
                  onChange={(e) => handlePreferenceChange('humidity', e.target.value)}
                />
                Humid
              </label>
              <label>
                <input
                  type="radio"
                  name="humidity"
                  value="dry"
                  checked={preferences.humidity === 'dry'}
                  onChange={(e) => handlePreferenceChange('humidity', e.target.value)}
                />
                Dry
              </label>
            </fieldset>

            {/* Sun/Clouds Preference */}
            <fieldset className="preference-group">
              <legend>Sun/Clouds</legend>
              <label>
                <input
                  type="radio"
                  name="sunClouds"
                  value="clear"
                  checked={preferences.sunClouds === 'clear'}
                  onChange={(e) => handlePreferenceChange('sunClouds', e.target.value)}
                />
                Clear Skies <small>(bright and sunny)</small>
              </label>
              <label>
                <input
                  type="radio"
                  name="sunClouds"
                  value="romanticClouds"
                  checked={preferences.sunClouds === 'romanticClouds'}
                  onChange={(e) => handlePreferenceChange('sunClouds', e.target.value)}
                />
                Romantic Clouds <small>(soft, dreamy overcast adding a touch of drama)</small>
              </label>
            </fieldset>

            {/* Sunset Preference */}
            <fieldset className="preference-group">
              <legend>Sunset (No earlier than...)</legend>
              <select
                value={preferences.sunset}
                onChange={(e) => handlePreferenceChange('sunset', e.target.value)}
              >
                <option value="">Select a time</option>
                <option value="6:00 PM">6:00 PM</option>
                <option value="6:30 PM">6:30 PM</option>
                <option value="7:00 PM">7:00 PM</option>
                <option value="7:30 PM">7:30 PM</option>
                <option value="8:00 PM">8:00 PM</option>
              </select>
            </fieldset>

            {/* Wind Preference */}
            <fieldset className="preference-group">
              <legend>Wind</legend>
              <label>
                <input
                  type="radio"
                  name="wind"
                  value="calm"
                  checked={preferences.wind === 'calm'}
                  onChange={(e) => handlePreferenceChange('wind', e.target.value)}
                />
                Calm
              </label>
              <label>
                <input
                  type="radio"
                  name="wind"
                  value="breeze"
                  checked={preferences.wind === 'breeze'}
                  onChange={(e) => handlePreferenceChange('wind', e.target.value)}
                />
                Light Breeze
              </label>
            </fieldset>

            {/* Precipitation Preference */}
            <fieldset className="preference-group">
              <legend>Light Precipitation</legend>
              <p className="helper-text">Would you like a gentle hint of rain to add a romantic ambiance to your wedding day?</p>
              <label>
                <input
                  type="radio"
                  name="precipitation"
                  value="yes"
                  checked={preferences.precipitation === 'yes'}
                  onChange={(e) => handlePreferenceChange('precipitation', e.target.value)}
                />
                Yes, a light drizzle for romance
              </label>
              <label>
                <input
                  type="radio"
                  name="precipitation"
                  value="no"
                  checked={preferences.precipitation === 'no'}
                  onChange={(e) => handlePreferenceChange('precipitation', e.target.value)}
                />
                No, I prefer dry conditions
              </label>
            </fieldset>

            {/* Date Range Preference */}
            <fieldset className="preference-group">
              <legend>Date Range</legend>
              <label>
                Start Date:
                <input
                  type="date"
                  name="startDate"
                  value={preferences.startDate}
                  min={today}
                  max="2025-10-31"
                  onChange={(e) => handlePreferenceChange('startDate', e.target.value)}
                />
              </label>
              <label>
                End Date:
                <input
                  type="date"
                  name="endDate"
                  value={preferences.endDate}
                  min={preferences.startDate}
                  max="2025-10-31"
                  onChange={(e) => handlePreferenceChange('endDate', e.target.value)}
                />
              </label>
            </fieldset>
          </form>

          <div className="next-step-container">
            <button className="next-step-button" onClick={handleSeeForecast}>
              See Dates
            </button>
          </div>
        </div>
      )}

      {/* 2) Show “fetching” section if isLoading is true */}
      {isLoading && (
        <div className="fetching-matches-container">
          <h1 className="fetching-title">Fetching Dates That Match Your Preferences...</h1>
          <p className="fetching-subtitle">
            Please wait while we analyze the weather forecasts.
          </p>
          <div className="spinner"></div>
        </div>
      )}

      {/* 3) Show the results if we have them and are not loading */}
      {!isLoading && showResults && forecastResults && (
        <div ref={forecastRef} className="preferences-page-container">
          {apiError && <p className="error">{apiError}</p>}

          <h2 className="page-title">Your Matching Dates & Venues</h2>
          <div className="venues-bucket">
            {forecastResults.map((result, index) => {
              return (
                <VenueCard
                  key={index}
                  venue={result.venue}
                  matches={result.matches}
                  startDate={preferences.startDate}
                  endDate={preferences.endDate}
                  onSelectDate={(selectedMatch) => {
                    setSelectedMatches((prev) => ({
                      ...prev,
                      [result.venue.name]: selectedMatch,
                    }));
                  }}
                />
              );
            })}
          </div>

          {/* Buttons to go back or continue */}
          <div className="navigation-buttons-container preferences-footer">
            <button
              type="button"
              className="step-btn--secondary"
              onClick={handleReturnToForm}
            >
              Edit Preferences
            </button>

            <button
              type="button"
              className="step-btn--primary"
              onClick={handleNext}
            >
              Continue to Dashboard
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Preferences;